// src/components/checkout/CartReview.jsx
import React from "react";
import "./CartReview.css";

const priceToNumber = (v) =>
  v == null ? 0 : (typeof v === "number" ? v : parseFloat(String(v).replace(",", ".").trim())) || 0;

const formatPLN = (n) => `${(n ?? 0).toFixed(2)} zł`;

const t = (v, lang = "pl") => {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number") return String(v);
  if (typeof v === "object") {
    return v[lang] || v.pl || v.en || v.it || Object.values(v).find((x) => typeof x === "string") || "";
  }
  return "";
};

const getBaseUnitPrice = (item) => {
  const bySize =
    item?.prices && item.selectedSize && item.prices[item.selectedSize] != null
      ? priceToNumber(item.prices[item.selectedSize])
      : null;
  return bySize != null ? bySize : priceToNumber(item?.price);
};

// NEW: normalize to an array of extra IDs
const getExtraIds = (item) =>
  Array.isArray(item.selectedExtras)
    ? item.selectedExtras
    : item.selectedExtra
    ? [item.selectedExtra]
    : [];

export default function CartReview({
  cartItems = [],
  ingredientsMap = {},
  extrasMap = {},             // << accept extrasMap
  deliveryFee,
  freeDeliveryThreshold,
  method,
  computedDeliveryFee,
}) {
  const lines = cartItems.map((item) => {
    const qty = Number(item.quantity) || 0;
    const unit = getBaseUnitPrice(item);

    const ingredientUnitSum = (item.selectedIngredients || []).reduce((sum, id) => {
      const ing = ingredientsMap?.[id];
      return sum + (ing ? priceToNumber(ing.price) : 0);
    }, 0);

    // NEW: extras price per unit
    const extrasUnitSum = getExtraIds(item).reduce((sum, id) => {
      const ex = extrasMap?.[id];
      const p = ex && (typeof ex._priceNumber === "number" ? ex._priceNumber : priceToNumber(ex?.price));
      return sum + (p || 0);
    }, 0);

    const lineTotal = qty * (unit + ingredientUnitSum + extrasUnitSum);
    return { item, qty, unit, ingredientUnitSum, extrasUnitSum, lineTotal };
  });

  const subtotal = lines.reduce((acc, l) => acc + l.lineTotal, 0);

  const methodNorm = (method || "").trim().toLowerCase();
  const safeDelivery = priceToNumber(deliveryFee);
  const threshold = priceToNumber(freeDeliveryThreshold); // 0 => no free delivery

  // honor parent fee if provided; else mirror backend rule
  const parentFee = priceToNumber(computedDeliveryFee);
  let deliveryToCharge;
  if (!Number.isNaN(parentFee) && parentFee !== 0) {
    deliveryToCharge = parentFee;
  } else if (methodNorm === "pickup") {
    deliveryToCharge = 0;
  } else if (methodNorm === "delivery") {
    deliveryToCharge = threshold > 0 ? (subtotal >= threshold ? 0 : safeDelivery) : safeDelivery;
  } else {
    deliveryToCharge = threshold > 0 ? (subtotal >= threshold ? 0 : safeDelivery) : safeDelivery;
  }

  const total = subtotal + deliveryToCharge;

  return (
    <div className="checkout-cart-review">
      <h3 className="checkout-cart-review-title">Twoje zamówienie</h3>

      <ul className="checkout-cart-review-list">
        {lines.map(({ item, qty, unit, ingredientUnitSum, extrasUnitSum }) => {
          const extraIds = getExtraIds(item);
          return (
            <li key={item.cartKey} className="checkout-cart-review-item">
              <p className="checkout-cart-review-line">
                <b>{qty}x</b> <b>{t(item?.name, "pl") || "Pozycja"}</b>
                {item?.selectedSize ? ` – ${t(item.selectedSize, "pl")}` : null}
                {" - "}
                {formatPLN(unit)}
              </p>

              <p className="checkout-cart-review-sub">
                {t(item?.variantText, "pl") ||
                  t(item?.description, "pl") ||
                  t(item?.setName, "pl") ||
                  "bez zestawu"}
              </p>

              {item.selectedIngredients?.length > 0 && (
                <ul className="checkout-cart-ingredient-list">
                  {item.selectedIngredients.map((id) => {
                    const ing = ingredientsMap[id];
                    if (!ing) return null;
                    const ingUnit = priceToNumber(ing.price);
                    return (
                      <li key={id} className="checkout-cart-ingredient-item">
                        {qty} × {t(ing?.name, "pl")} (+{formatPLN(ingUnit)})
                      </li>
                    );
                  })}
                </ul>
              )}

              {/* NEW: render extras list */}
              {extraIds.length > 0 && (
                <ul className="checkout-cart-ingredient-list">
                  {extraIds.map((id) => {
                    const ex = extrasMap[id];
                    if (!ex) return null;
                    const exUnit =
                      typeof ex._priceNumber === "number" ? ex._priceNumber : priceToNumber(ex.price);
                    return (
                      <li key={id} className="checkout-cart-ingredient-item">
                         {t(ex?.name, "pl") || "Dodatek"} (+{formatPLN(exUnit)})
                      </li>
                    );
                  })}
                </ul>
              )}

              <hr className="checkout-cart-divider" />
            </li>
          );
        })}
      </ul>

      <div className="checkout-cart-summary">
        <p className="checkout-cart-summary-row">
          <span>Suma częściowa:</span>
          <b>{formatPLN(subtotal)}</b>
        </p>
        <p className="checkout-cart-summary-row">
          <span>Koszt dostawy:</span>
          <b>{formatPLN(deliveryToCharge)}</b>
        </p>
        <p className="checkout-cart-summary-row checkout-cart-summary-total">
          <span>Razem:</span>
          <b>{formatPLN(total)}</b>
        </p>
      </div>
    </div>
  );
}
